import { useState } from 'react';

import type { AllowlistEntry } from '@/hooks/useAllowlist';
import { formatAddedDate } from '@/lib/formatters';

export function getDisplayName(entry: AllowlistEntry): string | null {
  return entry.name && entry.surname ? `${entry.name} ${entry.surname}` : null;
}

function getInitials(entry: AllowlistEntry): string {
  if (entry.name && entry.surname) {
    return `${entry.name[0] ?? ''}${entry.surname[0] ?? ''}`.toUpperCase();
  }
  return (entry.email[0] ?? '?').toUpperCase();
}

interface BetaUserRowProps {
  entry: AllowlistEntry;
  onRemove: (entry: AllowlistEntry) => void | Promise<void>;
  isRemoving: boolean;
}

// Design: Beta Users Admin — list row. Registered users (joined name
// present) show initials + name + email; email-only entries show a muted
// single-letter avatar and just the email. The trash button reveals on
// hover (and keyboard focus) and swaps the row to an inline confirm.
export function BetaUserRow({ entry, onRemove, isRemoving }: BetaUserRowProps) {
  const [confirmRemove, setConfirmRemove] = useState(false);

  const displayName = getDisplayName(entry);
  const label = displayName ?? entry.email;

  if (confirmRemove) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg bg-wolisho-red/[0.04] px-4 py-3"
        data-testid="beta-user-row-confirm"
        data-ph-mask
      >
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold text-wolisho-red">Remove {label}?</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setConfirmRemove(false)}
            data-testid="beta-user-cancel-remove"
            className="rounded-md border border-wolisho-brown/20 px-3.5 py-1.5 text-[13px] font-bold text-wolisho-brown"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void onRemove(entry);
              setConfirmRemove(false);
            }}
            disabled={isRemoving}
            data-testid="beta-user-confirm-remove"
            className={`rounded-md bg-wolisho-red px-3.5 py-1.5 text-[13px] font-bold text-white ${
              isRemoving ? 'opacity-60' : ''
            }`}
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group flex items-center gap-3 rounded-lg px-4 py-2.5 transition-colors hover:bg-wolisho-brown/[0.02]"
      data-testid="beta-user-row"
      data-ph-mask
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${
          displayName
            ? 'bg-stone-200 text-stone-600'
            : 'bg-wolisho-brown/[0.06] text-wolisho-brown/40'
        }`}
        aria-hidden="true"
      >
        {getInitials(entry)}
      </div>

      <div className="min-w-0 flex-1">
        {displayName && <p className="truncate text-sm font-bold text-black">{displayName}</p>}
        <p
          className={
            displayName ? 'truncate text-[13px] text-black/50' : 'truncate text-sm font-bold text-black'
          }
        >
          {entry.email}
        </p>
      </div>

      <span className="shrink-0 text-xs text-black/35">{formatAddedDate(entry.addedAt)}</span>

      <button
        type="button"
        onClick={() => setConfirmRemove(true)}
        // Generic label (no email/name): session replay records element
        // attributes, and `maskTextSelector` only masks text nodes — an
        // interpolated address here would leak PII into the recording.
        aria-label="Remove user"
        data-testid="beta-user-remove"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-wolisho-brown opacity-0 transition-opacity focus-visible:opacity-50 group-hover:opacity-50 [@media(hover:none)]:opacity-50"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
        </svg>
      </button>
    </div>
  );
}
